/**
 * TomlAbilityFactory — Auto-registers TOML abilities that lack manual implementations.
 *
 * Reads ABILITY_DATA and creates generic AbilityBuilder abilities for every entry
 * not already in the AbilityRegistry. Uses abilityType, mechanicType, target, school,
 * and VFX fields from TOML to produce appropriate behavior (projectile, melee, heal,
 * buff, debuff, AoE, etc.).
 *
 * Import this AFTER all manual registration files so it only fills gaps.
 */

import { playerCombatPoint } from '../../../../shared/logic/CombatAnchors';
import { ABILITY_DATA, type AbilityDataDef } from '../../../../shared/content/generated/abilities.gen';
import { abilityRegistry, type AbilityFunction } from './AbilityRegistry';
import { createAbility } from './AbilityBuilder';
import { gameLogger } from '../../../../utils/GameLogger';
import { EventBus } from '../../../../shared/EventBus';
import { calcAbilityDamage } from '../../../../shared/content/AbilityDataAdapter';
import { usesProjectileDelivery } from '../../../../shared/content/AbilityDelivery';
import { ABILITY_CONSTANTS } from '../../../../shared/constants/AbilityConstants';
import { deriveProjectileSize } from '../../../../shared/config/projectile-vfx.config';
// School → projectile hex color (shared SSOT, also used by RemoteAbilityVFX).
import { serverOwnsHealth } from './behaviors/serverOwnsHealth';
import { basicAttackDamage } from './basicAttackDamage';
import { DATA_TRAIL_WIDTH, tomlProjectileColor } from './abilityDataTrail';

// School → valid impact effect type for withImpactEffect()
const SCHOOL_IMPACT: Record<string, 'explosion' | 'spark' | 'shatter' | 'freeze' | 'burn'> = {
  physical: 'spark',
  fire:     'burn',
  frost:    'freeze',
  arcane:   'explosion',
  shadow:   'shatter',
  holy:     'explosion',
  nature:   'spark',
  chaos:    'explosion',
  healing:  'spark',
};

function parseColor(hex: string | undefined): number {
  if (!hex) return 0xcccccc;
  return parseInt(hex.replace('#', ''), 16);
}

export function buildGenericAbility(data: AbilityDataDef): void {
  const { id, name, school, abilityType, target } = data;
  const color = tomlProjectileColor(data);
  const impact = SCHOOL_IMPACT[school || 'physical'] || 'spark';
  const element = (school === 'healing' ? 'holy' : school || 'physical') as any;

  const builder = createAbility(id)
    .withName(name)
    .withTomlData(id)
    .withElement(element);

  // Route by ability type + target to choose the right behavior
  if (abilityType === 'heal') {
    builder.withHeal({
      amount: (ctx) => {
        const maxHp = ctx.currentPlayer.stats?.maxHp ?? ctx.currentPlayer.maxHealth ?? 100;
        const int = ctx.currentPlayer.stats?.intelligence ?? 0;
        const base = data.baseDamage ?? 20;
        return Math.floor(base + maxHp * 0.15 + int * 1.0);
      },
      target: 'self',
      vfxId: data.vfx?.castEffect || 'holy_heal',
    });
    // HoT visual (K20 renew, 2026-07-17): a heal with a duration + 'hot' tag
    // is a HEAL-OVER-TIME, but withHeal only plays the one-shot heal splash —
    // the 9s of healing rendered nothing. Ride the status-aura pipeline for
    // the duration ('regen' → status_regen via STATUS_AURA_CONFIG unless the
    // TOML authors its own cast_effect).
    // Plan 29 F9 (lane CA): online the server's heal-over-time status draws
    // it on whoever it heals, on every screen (its data names the ability →
    // STATUS_PRESENTATIONS); this local copy drew on the CASTER even when a
    // friend was healed. It stays for offline play only.
    const isHot = (data.durationMs ?? 0) > 0 && (data.tags ?? []).includes('hot');
    if (isHot) {
      const hotDuration = data.durationMs!;
      const hotEffectId = data.vfx?.castEffect || 'regen';
      builder.withInstantEffect((ctx) => {
        if (serverOwnsHealth()) return;
        const targetId = ctx.currentPlayer.id;
        EventBus.getInstance().emit('vfx:status_effect', {
          targetId, effectId: hotEffectId, abilityId: id, active: true, duration: hotDuration,
        });
        setTimeout(() => {
          EventBus.getInstance().emit('vfx:status_effect', { targetId, effectId: hotEffectId, active: false });
        }, hotDuration);
      });
    }
  } else if (abilityType === 'buff') {
    const duration = data.durationMs ?? 10000;
    const stat = data.defenseBonus ? 'defenseMultiplier' : data.attackBonus ? 'attackMultiplier' : 'defenseMultiplier';
    const value = data.defenseBonus ? (data.defenseBonus / 100) : data.attackBonus ? (data.attackBonus / 100) : 0.2;
    // Authored aura channel (VQA sprint U-vfx-authority-2): when the TOML
    // authors `aura_color`, don't force the generic 'defensive_aura' —
    // an always-registered explicit id wins resolveStatusVfxId, shadowing
    // the ability's own registered `${id}_aura`. Leave the explicit slot to
    // the authored cast_effect (or empty) so resolution falls through to
    // metadata.vfx.aura, and tint with the authored aura color.
    const auraColor = data.vfx?.auraColor;
    builder.withBuff({
      stat,
      value,
      duration,
      vfx: {
        effectId: data.vfx?.castEffect || (auraColor ? undefined : 'defensive_aura'),
        color: auraColor ? parseColor(auraColor) : color,
        duration,
      },
    });
  } else if (abilityType === 'debuff') {
    builder.withDebuff({
      stat: 'speedMultiplier',
      value: data.slowValue ? -(data.slowValue / 100) : -0.3,
      durationMs: data.slowDurationMs ?? data.durationMs ?? 5000,
      // Target-anchored debuff aura (U-vfx-authority-2): without a vfxId the
      // DebuffBehavior renders nothing on the victim — authored curse/slow
      // marks never showed. Prefer the authored cast_effect, else the
      // `${id}_aura` registered from TOML aura_color.
      vfxId: data.vfx?.castEffect ?? (data.vfx?.auraColor ? `${id}_aura` : undefined),
    });
    if (usesProjectileDelivery(data)) {
      // Ranged debuffs (curses, slows) should show a projectile even when they
      // have no damage component. The debuff applies at cast time; the
      // projectile is visual-only and records a clean impact for VFX QA.
      const shape = (data.vfx?.projectileShape as any) ?? 'circle';
      const size = deriveProjectileSize(data.vfx?.projectileSize ?? 20, shape);
      const hasDamage = data.baseDamage && data.baseDamage > 0;
      builder.withProjectile({
        damage: hasDamage
          ? (ctx) => {
            const stat = ctx.currentPlayer.stats?.magicAttack ?? ctx.currentPlayer.stats?.attack ?? 0;
            return calcAbilityDamage(id, stat, ctx.currentPlayer.level, { levelScaling: 0 });
          }
          : 0,
        speed: data.vfx?.projectileSpeed ?? 350,
        shape,
        color,
        width: size.width,
        height: size.height,
        lifespan: data.vfx?.projectileLifespan ?? 2000,
      });
    }
  } else if (abilityType === 'defensive') {
    const duration = data.durationMs ?? 8000;
    builder.withShield({
      amount: Math.floor((data.defenseBonus ?? 20) + (data.baseDamage ?? 50)),
      durationMs: duration,
      vfxId: data.vfx?.castEffect || 'defensive_aura',
    });
  } else if (abilityType === 'utility' && target === 'self') {
    // F5-014: self-target utilities (cleansing_bloom, blink, vanish, evocation,
    // …14 abilities) must never fall through to the offensive tiers — the
    // school tiebreaker in usesProjectileDelivery was routing them as live enemy
    // projectiles (a nature "dispel" dealt projectile damage). Emit the cast
    // VFX on the caster only; the actual mechanic (teleport, stealth, mana
    // restore, dispel) is server-side. Offensive self-centered abilities
    // (warpath, retribution_strike) keep their damage routing below.
    builder.withInstantEffect((ctx) => {
      const player = ctx.currentPlayer as any;
      EventBus.getInstance().emit('vfx:spawn', {
        effectId: data.vfx?.castEffect || 'sparkle_burst',
        ...playerCombatPoint(player), // plan 29 F7: the body centre, not the hitbox corner
        duration: 300,
        abilityId: id,
        casterId: player.id,
        follow: false,
      });
    });
  } else if (id === 'shadow_step') {
    // Shadow Step is a pure movement utility: the factory would otherwise
    // route it as a projectile attack because its school is 'shadow', and the
    // authored cast_effect would never fire. Emit the cast VFX only — no
    // damage or movement logic; teleport is handled server-side.
    builder.withInstantEffect((ctx) => {
      const player = ctx.currentPlayer as any;
      EventBus.getInstance().emit('vfx:spawn', {
        effectId: data.vfx?.castEffect || 'shadow_teleport',
        ...playerCombatPoint(player), // plan 29 F7: the body centre, not the hitbox corner
        duration: 300,
        abilityId: id,
        casterId: player.id,
        follow: false,
      });
    });
  } else if (target === 'aoe' || target === 'ground') {
    // AoE offensive
    builder.withAOE({
      damage: (ctx) => {
        const stat = ctx.currentPlayer.stats?.magicAttack ?? ctx.currentPlayer.stats?.attack ?? 0;
        return calcAbilityDamage(id, stat, ctx.currentPlayer.level, { levelScaling: ABILITY_CONSTANTS.LEVEL_SCALING_AOE });
      },
      radius: data.radius ?? 120,
      vfxId: data.vfx?.castEffect || 'aoe_blast',
    });
  } else if (usesProjectileDelivery(data)) {
    // Projectile attack
    const speed = data.vfx?.projectileSpeed ?? 400;
    const shape = (data.vfx?.projectileShape as any) ?? (school === 'physical' ? 'arrow' : 'circle');
    // Scalar projectileSize → width/height via the shape's natural aspect
    // (arrow 35:3, star 35:15, round 1:1). A physical-school auto-shape arrow
    // now renders thin from a single scalar instead of scalar-to-both square.
    const size = deriveProjectileSize(data.vfx?.projectileSize ?? 24, shape);
    builder.withProjectile({
      damage: (ctx) => {
        // Priest is the only basic implemented by this factory. Keep every
        // other factory projectile's authored formula and level term unchanged.
        if (id === 'priest_auto') return basicAttackDamage(id, ctx.currentPlayer);
        const stat = ctx.currentPlayer.stats?.magicAttack ?? ctx.currentPlayer.stats?.attack ?? 0;
        return calcAbilityDamage(id, stat, ctx.currentPlayer.level, { levelScaling: ABILITY_CONSTANTS.LEVEL_SCALING_RANGED });
      },
      speed,
      shape,
      color,
      width: size.width,
      height: size.height,
      lifespan: data.vfx?.projectileLifespan ?? ABILITY_CONSTANTS.PROJECTILE_LIFESPAN_DEFAULT,
      homing: { strength: ABILITY_CONSTANTS.HOMING_STRENGTH_DEFAULT, maxTurnRate: ABILITY_CONSTANTS.HOMING_MAX_TURN_RATE, delay: ABILITY_CONSTANTS.HOMING_DELAY_DEFAULT },
    });
    // TOML `vfx.trail` (boolean | string, trails spec §5):
    //   true / omitted → default school ribbon (auto-registered def)
    //   "ribbon"       → explicit default ribbon (same as true)
    //   "<def_id>"     → a registered trail-category def with a ribbon prim
    //   false          → no trail
    const trail = data.vfx?.trail;
    if (typeof trail === 'string' && trail !== 'ribbon') {
      builder.withTrailDef(trail);
    } else if (trail !== false) {
      builder.withTrail(color, DATA_TRAIL_WIDTH);
    }
    if (!data.vfx?.impactEffect) {
      builder.withImpactEffect(impact);
    }
    // Plan 29 F9 (lane CA): an instant TOML projectile's cast_effect is its
    // muzzle flash (castGeometry.projectile.muzzle — observers draw it too);
    // with a wind-up, ProjectileBehavior draws it as the charge instead.
    if (data.vfx?.castEffect && !(data.windUpMs && data.windUpMs > 0)) {
      builder.withVFX({ effectId: data.vfx.castEffect, color, duration: 200 });
    }
  } else {
    // Melee attack (default for physical / close-range)
    builder.withMelee({
      damage: (ctx) => {
        const attack = ctx.currentPlayer.stats?.attack ?? 0;
        return calcAbilityDamage(id, attack, ctx.currentPlayer.level, { levelScaling: ABILITY_CONSTANTS.LEVEL_SCALING_MELEE });
      },
      range: data.range ?? ABILITY_CONSTANTS.MELEE_RANGE_DEFAULT,
      vfxId: data.vfx?.impactEffect || 'melee_slash',
    });
  }

  // Wind-up if the ability has one
  if (data.windUpMs && data.windUpMs > 0) {
    // Plan 29 F9 (lane CA): `wind_up_effect` is the tell when it differs from
    // the cast (an area heal draws its cast at the heal radius after it).
    builder.withWindUp(data.windUpMs, data.vfx?.windUpEffect ?? data.vfx?.castEffect);
  }

  const fn = builder.build();

  // Register in the ability registry
  abilityRegistry.register({
    id,
    fn,
    tags: [data.class, 'toml-factory', ...(data.tags || [])],
    name,
  });
}

function registerAutoAttacks(): void {
  for (const id of Object.keys(ABILITY_DATA)) {
    if (!id.endsWith('_auto')) continue;
    const className = id.replace('_auto', '');
    if (abilityRegistry.getAutoAttack(className)) continue;
    const fn = abilityRegistry.get(id);
    if (fn) {
      abilityRegistry.registerAutoAttack(className, fn);
    }
  }
}

export interface RegisterTomlOptions {
  /** If set, only register auto-attacks and abilities for this class. */
  onlyClass?: string;
  /** If set, only register auto-attacks for every class. */
  onlyAutoAttacks?: boolean;
}

/**
 * Eagerly register TOML abilities. Call once at boot for the current player's
 * class, then optionally again later (or rely on `ensureTomlAbility`) for the
 * remaining classes. Returns the number of newly registered abilities.
 */
export function registerTomlAbilities(options: RegisterTomlOptions = {}): number {
  const targetClass = options.onlyClass?.toLowerCase();
  let registered = 0;

  for (const [id, data] of Object.entries(ABILITY_DATA)) {
    if (abilityRegistry.isRegistered(id)) continue;
    if (id.endsWith('_auto')) continue; // handled by registerAutoAttacks
    if (options.onlyAutoAttacks) continue;
    if (targetClass && data.class?.toLowerCase() !== targetClass) continue;

    try {
      buildGenericAbility(data);
      registered++;
    } catch (e: any) {
      gameLogger.warn('abilities', `TomlAbilityFactory: failed to register "${id}": ${e.message}`);
    }
  }

  registerAutoAttacks();

  if (registered > 0) {
    gameLogger.info('abilities', `TomlAbilityFactory: registered ${registered} generic abilities from TOML data${targetClass ? ` for class ${targetClass}` : ''}`);
  }
  return registered;
}

/**
 * Lazily build a single TOML ability on first lookup. Registered as the ability
 * registry's TOML resolver.
 */
export function ensureTomlAbility(id: string): AbilityFunction | undefined {
  if (abilityRegistry.isRegistered(id)) return abilityRegistry.get(id);
  const data = ABILITY_DATA[id];
  if (!data) return undefined;
  try {
    buildGenericAbility(data);
  } catch (e: any) {
    gameLogger.warn('abilities', `TomlAbilityFactory: lazy registration failed for "${id}": ${e.message}`);
    return undefined;
  }
  return abilityRegistry.get(id);
}

// Wire the lazy resolver once at module load. Heavy eager registration is now
// opt-in via `registerTomlAbilities()`; this resolver covers on-demand usage.
abilityRegistry.setTomlResolver(ensureTomlAbility);
